import { iterateAttachmentParts } from './message-attachments';
import { readLegacyUploadedFileMetadata, remapLegacyUploadedFileReferences } from './legacy-uploaded-file-content';
import { createLegacyUploadedFileId } from './legacy-uploaded-file-id';
import { generateId } from '@/01-models/id';
import { idToRaw } from '@/01-models/ids';
import type { BinaryObjectId, ChatGroupId, ChatId, VolumeId } from '@/01-models/ids';
import type { Chat, Settings, ChatGroup, SidebarItem, MessageNode, ChatMeta, ChatContent, StorageSnapshot, BinaryObject, Volume, VolumeType } from '@/01-models/types';
import {
  type ChatMetaDto,
  type ChatGroupDto,
  type HierarchyDto,
  type MigrationChunkDto,
  ChatMetaSchemaDto,
  ChatGroupSchemaDto,
  SettingsSchemaDto,
  HierarchySchemaDto,
  ChatContentSchemaDto,
  type VolumeDto,
  type VolumeIndexDto,
  VolumeIndexSchemaDto,
} from '@/00-storage/00-dto/dto';
import {
  chatToDomain,
  chatToDto,
  chatGroupToDomain,
  chatGroupConfigurationToDomain,
  chatGroupToDto,
  settingsToDomain,
  settingsToDto,
  hierarchyToDomain,
  hierarchyToDto,
  chatMetaToDto,
  chatMetaToDomain,
  chatContentToDto,
  chatContentToDomain,
  buildSidebarItemsFromHierarchy,
  binaryObjectToDomain,
  volumeToDomain,
  volumeToDto,
} from '@/00-storage/mapper/mappers';
import { IStorageProvider } from './interface';

import {
  type MigrationStateDto,
  type BinaryShardIndexDto,
  MigrationStateSchemaDto,
  BinaryShardIndexSchemaDto,
} from '@/00-storage/00-dto/dto';
import { toBinaryObjectId, toChatGroupId, toChatId } from '@/01-models/ids';
import { promiseAllKeyed } from '@/utils/promise';
import { ExperimentalNaidanRpcRegistrySchemaDto, type ExperimentalNaidanRpcRegistryDto } from '@/00-storage/00-dto/experimental-naidan-rpc.dto';
import type { StoredVolumeAccess } from '@/01-models/volume-access';
import type { StorageSynchronizer } from './synchronizer';

interface FileSystemFileHandleWithWritable extends FileSystemFileHandle {
  createWritable(): Promise<FileSystemWritableFileStream>,
}

const MIGRATION_V1_UPLOADED_FILES_TO_BINARY_OBJECTS = 'v1_uploaded_files_to_binary_objects';
const NAIDAN_RPC_REGISTRY_FILE = 'naidan-rpc-connections.json';

type BinaryShardIndex = BinaryShardIndexDto;

let volumeSynchronizer: StorageSynchronizer | undefined;

async function getVolumeSynchronizer(): Promise<StorageSynchronizer> {
  const { StorageSynchronizer } = await import('./synchronizer');
  volumeSynchronizer ??= new StorageSynchronizer();
  return volumeSynchronizer;
}

function isVolumeEntryNotFound({ error }: { error: unknown }): boolean {
  return (error instanceof Error || error instanceof DOMException) && error.name === 'NotFoundError';
}

export class OPFSStorageProvider extends IStorageProvider {
  private root: FileSystemDirectoryHandle | null = null;
  private readonly STORAGE_DIR = 'naidan-storage';
  readonly canPersistBinary = true;

  async loadNaidanRpcRegistry(): Promise<ExperimentalNaidanRpcRegistryDto | undefined> {
    const directory = await this.getExperimentalDir({ create: false });
    if (!directory) {
      return undefined;
    }
    let handle: FileSystemFileHandle;
    try {
      handle = await directory.getFileHandle(NAIDAN_RPC_REGISTRY_FILE);
    } catch (error) {
      if ((error instanceof DOMException || error instanceof Error) && error.name === 'NotFoundError') {
        return undefined;
      }
      throw error;
    }
    return ExperimentalNaidanRpcRegistrySchemaDto.parse(JSON.parse(await (await handle.getFile()).text()));
  }

  async saveNaidanRpcRegistry({ registry }: { registry: ExperimentalNaidanRpcRegistryDto | undefined }): Promise<void> {
    if (registry === undefined) {
      const directory = await this.getExperimentalDir({ create: false });
      if (!directory) {
        return;
      }
      try {
        await directory.removeEntry(NAIDAN_RPC_REGISTRY_FILE);
      } catch (error) {
        if (!((error instanceof DOMException || error instanceof Error) && error.name === 'NotFoundError')) {
          throw error;
        }
      }
      return;
    }
    const text = JSON.stringify(ExperimentalNaidanRpcRegistrySchemaDto.parse(registry));
    const directory = await this.getExperimentalDir({ create: true });
    if (!directory) {
      throw new Error('Experimental storage directory is unavailable.');
    }
    let handle: FileSystemFileHandle;
    let created = false;
    try {
      handle = await directory.getFileHandle(NAIDAN_RPC_REGISTRY_FILE);
    } catch (error) {
      if (!((error instanceof DOMException || error instanceof Error) && error.name === 'NotFoundError')) {
        throw error;
      }
      handle = await directory.getFileHandle(NAIDAN_RPC_REGISTRY_FILE, { create: true });
      created = true;
    }
    let writable: FileSystemWritableFileStream | undefined;
    try {
      writable = await handle.createWritable();
      await writable.write(text);
      await writable.close();
    } catch (error) {
      try {
        await writable?.abort();
      } catch { /* Preserve the original save failure. */ }
      if (created) {
        try {
          await directory.removeEntry(NAIDAN_RPC_REGISTRY_FILE);
        } catch { /* Cleanup failure does not turn a failed save into success. */ }
      }
      throw error;
    }
  }

  private async getExperimentalDir({ create }: { create: boolean }): Promise<FileSystemDirectoryHandle | undefined> {
    await this.ensureRoot();
    try {
      return await this.root!.getDirectoryHandle('experimental', { create });
    } catch (error) {
      if (!create && (error instanceof DOMException || error instanceof Error) && error.name === 'NotFoundError') {
        return undefined;
      }
      throw error;
    }
  }

  private async readChatRecord({ directory, id }: {
    directory: 'chat-metas' | 'chat-contents',
    id: ChatId,
  }): Promise<string | null> {
    await this.ensureRoot();
    const dir = await this.getDir({ name: directory });
    let fileHandle: FileSystemFileHandle;
    try {
      fileHandle = await dir.getFileHandle(`${idToRaw({ id })}.json`);
    } catch (error) {
      // Only a missing entry at lookup means absence. An acquired handle that
      // later fails to read must never let an updater overwrite an empty chat.
      if ((error instanceof DOMException || error instanceof Error) && error.name === 'NotFoundError') {
        return null;
      }
      throw error;
    }
    const file = await fileHandle.getFile();
    return file.text();
  }

  private async loadUnhydratedChatContent({ id }: { id: ChatId }): Promise<ChatContent | null> {
    const rawContent = await this.readChatRecord({ directory: 'chat-contents', id });
    if (rawContent === null) {
      return null;
    }
    return chatContentToDomain({
      dto: ChatContentSchemaDto.parse(JSON.parse(rawContent)),
    });
  }

  async init(): Promise<void> {
    await this.ensureRoot();
    await this.runMigrations();
  }

  private async ensureRoot(): Promise<void> {
    if (!this.root) {
      const opfsRoot = await navigator.storage.getDirectory();
      this.root = await opfsRoot.getDirectoryHandle(this.STORAGE_DIR, { create: true });
    }
  }

  private async loadMigrationState(): Promise<MigrationStateDto> {
    try {
      const fileHandle = await this.root!.getFileHandle('migration-state.json');
      const file = await fileHandle.getFile();
      return MigrationStateSchemaDto.parse(JSON.parse(await file.text()));
    } catch {
      return { completedMigrations: [] };
    }
  }

  private async saveMigrationState({ state }: { state: MigrationStateDto }): Promise<void> {
    const fileHandle = await this.root!.getFileHandle('migration-state.json', { create: true }) as FileSystemFileHandleWithWritable;
    const writable = await fileHandle.createWritable();
    await writable.write(JSON.stringify(state));
    await writable.close();
  }

  private async runMigrations(): Promise<void> {
    const state = await this.loadMigrationState();
    const completed = new Set(state.completedMigrations.map(m => m.name));

    if (!completed.has(MIGRATION_V1_UPLOADED_FILES_TO_BINARY_OBJECTS)) {
      const outcome = await this.migrateV1UploadedFilesToBinaryObjects();
      // Unreadable or unwritten content may still reference the legacy directory.
      // Leave it retryable while allowing unrelated readable chats to be used.
      switch (outcome) {
      case 'deferred': return;
      case 'completed': break;
      default: {
        const _ex: never = outcome;
        throw new Error(`Unhandled migration outcome: ${_ex}`);
      }
      }
      state.completedMigrations.push({
        name: MIGRATION_V1_UPLOADED_FILES_TO_BINARY_OBJECTS,
        completedAt: Date.now(),
      });
      await this.saveMigrationState({ state });
    }
  }

  private async migrateV1UploadedFilesToBinaryObjects(): Promise<'completed' | 'deferred'> {
    let legacyDir: FileSystemDirectoryHandle;
    try {
      legacyDir = await this.root!.getDirectoryHandle('uploaded-files');
    } catch (error) {
      // Only failure to locate the source directory means migration is unnecessary.
      // A missing file later in the copy/write sequence must not mark success.
      const isNotFound = error instanceof Error && (error.name === 'NotFoundError' || ('code' in error && error.code === 8));
      if (isNotFound) {
        return 'completed';
      }
      throw error;
    }
    try {
      console.log(`[OPFSStorageProvider] Starting migration: ${MIGRATION_V1_UPLOADED_FILES_TO_BINARY_OBJECTS}`);

      // Read one document at a time; keep only metadata, not all conversation bodies.
      const contentDir = await this.getDir({ name: 'chat-contents' });
      const metadata = new Map<string, Map<string, { mimeType: string, createdAt: number } | undefined>>();
      let deferred = false;
      for await (const entry of contentDir.values()) {
        const entryKind = entry.kind;
        switch (entryKind) {
        case 'file':
          if (entry.name.endsWith('.json')) {
            try {
              const file = await (entry as FileSystemFileHandle).getFile();
              for (const { attachmentId, name, mimeType, createdAt } of readLegacyUploadedFileMetadata({ serialized: await file.text() })) {
                let files = metadata.get(attachmentId);
                if (!files) {
                  files = new Map();
                  metadata.set(attachmentId, files);
                }
                const previous = files.get(name);
                if (files.has(name) && (previous?.mimeType !== mimeType || previous.createdAt !== createdAt)) {
                  // One binary index cannot preserve contradictory legacy metadata.
                  files.set(name, undefined);
                  deferred = true;
                } else {
                  files.set(name, { mimeType, createdAt });
                }
              }
            } catch (error) {
              deferred = true;
              console.warn(`[OPFSStorageProvider] Cannot read legacy attachment metadata: ${entry.name}`, error);
            }
          }
          break;
        case 'directory': deferred = true;
          break;
        default: {
          const _ex: never = entryKind;
          throw new Error(`Unhandled content entry: ${_ex}`);
        }
        }
      }

      // 1. Copy each source file to a stable migration ID before changing references.
      const idMap = new Map<string, Map<string, string>>();

      for await (const attachmentDirEntry of legacyDir.values()) {
        const entryKind = attachmentDirEntry.kind;
        switch (entryKind) {
        case 'directory': {
          const attachmentId = attachmentDirEntry.name;
          for await (const fileEntry of (attachmentDirEntry as FileSystemDirectoryHandle).values()) {
            const fileKind = fileEntry.kind;
            switch (fileKind) {
            case 'file': {
              const blob = await (fileEntry as FileSystemFileHandle).getFile();
              const sourceMetadata = metadata.get(attachmentId);
              const recorded = sourceMetadata?.get(fileEntry.name);
              if (sourceMetadata?.has(fileEntry.name) && recorded === undefined) {
                // Retain the source and its references for a later explicit repair.
                continue;
              }
              const newBinaryObjectId = await createLegacyUploadedFileId({ attachmentId, name: fileEntry.name });
              const existing = await this.getBinaryObject({ binaryObjectId: newBinaryObjectId });
              await this.saveFileWithMetadata({
                blob,
                binaryObjectId: newBinaryObjectId,
                name: fileEntry.name,
                mimeType: recorded?.mimeType ?? existing?.mimeType,
                createdAt: recorded?.createdAt ?? existing?.createdAt ?? Date.now(),
              });
              let files = idMap.get(attachmentId);
              if (!files) {
                files = new Map();
                idMap.set(attachmentId, files);
              }
              files.set(fileEntry.name, idToRaw({ id: newBinaryObjectId }));
              break;
            }
            case 'directory':
              deferred = true;
              break;
            default: {
              const _ex: never = fileKind;
              throw new Error(`Unhandled file kind: ${_ex}`);
            }
            }
          }
          break;
        }
        case 'file':
          deferred = true;
          break;
        default: {
          const _ex: never = entryKind;
          throw new Error(`Unhandled entry kind: ${_ex}`);
        }
        }
      }

      // 2. Reread current content and rewrite only references whose copies succeeded.
      for await (const entry of contentDir.values()) {
        const entryKind = entry.kind;
        switch (entryKind) {
        case 'file': {
          if (entry.name.endsWith('.json')) {
            try {
              const file = await (entry as FileSystemFileHandle).getFile();
              const rewritten = remapLegacyUploadedFileReferences({
                serialized: await file.text(),
                binaryObjectIds: idMap,
              });
              if (rewritten.unresolvedReferences > 0) {
                deferred = true;
              }
              if (rewritten.serialized !== undefined) {
                const writable = await (entry as FileSystemFileHandleWithWritable).createWritable();
                try {
                  await writable.write(rewritten.serialized);
                  await writable.close();
                } catch (error) {
                  try {
                    await writable.abort();
                  } catch { /* The original failure remains authoritative. */ }
                  throw error;
                }
              }
            } catch (jsonErr) {
              deferred = true;
              console.warn(`[OPFSStorageProvider] Retaining legacy files because chat content could not be migrated: ${entry.name}`, jsonErr);
            }
          }
          break;
        }
        case 'directory':
          deferred = true;
          break;
        default: {
          const _ex: never = entryKind;
          throw new Error(`Unhandled entry kind: ${_ex}`);
        }
        }
      }

      // Do not delete a directory that may still be needed by failed documents.
      if (deferred) {
        return 'deferred';
      }

      // 3. Cleanup, after every inspected chat reference was committed.
      await this.root!.removeEntry('uploaded-files', { recursive: true });
      console.log(`[OPFSStorageProvider] Migration completed: ${MIGRATION_V1_UPLOADED_FILES_TO_BINARY_OBJECTS}`);
      return 'completed';
    } catch (error) {
      console.error(`[OPFSStorageProvider] Migration failed: ${MIGRATION_V1_UPLOADED_FILES_TO_BINARY_OBJECTS}`, error);
      throw error;
    }
  }

  private async getDir({ name, parent = this.root! }: { name: string, parent?: FileSystemDirectoryHandle }): Promise<FileSystemDirectoryHandle> {
    await this.ensureRoot();
    return await parent.getDirectoryHandle(name, { create: true });
  }

  // --- Binary Object Storage (Sharded) ---

  private getBinaryObjectShardPath({ id }: { id: BinaryObjectId }): string {
    return idToRaw({ id }).slice(-2).toLowerCase();
  }

  private async getBinaryObjectsDir(): Promise<FileSystemDirectoryHandle> {
    return await this.getDir({ name: 'binary-objects' });
  }

  private async getShardDir({ shard }: { shard: string }): Promise<FileSystemDirectoryHandle> {
    const baseDir = await this.getBinaryObjectsDir();
    return await this.getDir({ name: shard, parent: baseDir });
  }

  private async loadShardIndex({ shard }: { shard: string }): Promise<BinaryShardIndex> {
    const dir = await this.getShardDir({ shard });
    let fileHandle: FileSystemFileHandle;
    try {
      fileHandle = await dir.getFileHandle('index.json');
    } catch (error) {
      const isNotFound = (error instanceof DOMException || error instanceof Error)
        && (error.name === 'NotFoundError' || ('code' in error && error.code === 8));
      if (isNotFound) {
        return { objects: {} };
      }
      throw error;
    }
    // Invalid or unreadable metadata is not an empty index to overwrite.
    const file = await fileHandle.getFile();
    return BinaryShardIndexSchemaDto.parse(JSON.parse(await file.text()));
  }

  private async saveShardIndex({ shard, index }: { shard: string, index: BinaryShardIndex }): Promise<void> {
    const dir = await this.getShardDir({ shard: shard });
    const fileHandle = await dir.getFileHandle('index.json', { create: true }) as FileSystemFileHandleWithWritable;
    const writable = await fileHandle.createWritable();
    await writable.write(JSON.stringify(index));
    await writable.close();
  }

  private async hydrateAttachments({ nodes }: { nodes: MessageNode[] }): Promise<void> {
    const shardCache = new Map<string, BinaryShardIndex>();

    for (const part of iterateAttachmentParts({ nodes })) {
      const att = part.attachment;
      switch (att.status) {
      case 'persisted': {
        const shard = this.getBinaryObjectShardPath({ id: att.binaryObjectId });
        let index = shardCache.get(shard);
        if (!index) {
          index = await this.loadShardIndex({ shard });
          shardCache.set(shard, index);
        }
        const meta = index.objects[idToRaw({ id: att.binaryObjectId })];
        if (meta) {
          att.mimeType = meta.mimeType;
          att.size = meta.size;
          att.uploadedAt = meta.createdAt;
        } else {
          part.attachment = { ...att, status: 'missing' };
        }
        break;
      }
      case 'memory':
      case 'missing':
        break;
      default: {
        const _ex: never = att;
        throw new Error(`Unhandled attachment status: ${_ex}`);
      }
      }
    }
  }

  // --- Internal Data Access ---

  protected async listChatMetasRaw(): Promise<ChatMetaDto[]> {
    await this.ensureRoot();
    const dir = await this.getDir({ name: 'chat-metas' });
    const dtos: ChatMetaDto[] = [];
    for await (const entry of dir.values()) {
      const kind = entry.kind;
      switch (kind) {
      case 'file': {
        if (entry.name.endsWith('.json')) {
          const file = await (entry as FileSystemFileHandle).getFile();
          dtos.push(ChatMetaSchemaDto.parse(JSON.parse(await file.text())));
        }
        break;
      }
      case 'directory':
        break;
      default: {
        const _ex: never = kind;
        throw new Error(`Unhandled entry kind: ${_ex}`);
      }
      }
    }
    return dtos;
  }

  protected async listChatGroupsRaw(): Promise<ChatGroupDto[]> {
    await this.ensureRoot();
    const dir = await this.getDir({ name: 'chat-groups' });
    const dtos: ChatGroupDto[] = [];
    for await (const entry of dir.values()) {
      const kind = entry.kind;
      switch (kind) {
      case 'file': {
        if (entry.name.endsWith('.json')) {
          const file = await (entry as FileSystemFileHandle).getFile();
          dtos.push(ChatGroupSchemaDto.parse(JSON.parse(await file.text())));
        }
        break;
      }
      case 'directory':
        break;
      default: {
        const _ex: never = kind;
        throw new Error(`Unhandled entry kind: ${_ex}`);
      }
      }
    }
    return dtos;
  }

  // --- Hierarchy Management ---

  async loadHierarchy(): Promise<HierarchyDto | null> {
    await this.ensureRoot();
    let fileHandle: FileSystemFileHandle;
    try {
      fileHandle = await this.root!.getFileHandle('hierarchy.json');
    } catch (error) {
      // Only a missing lookup proves empty history; unreadable history must not
      // permit automatic creation to replace an existing hierarchy.
      if ((error instanceof DOMException || error instanceof Error) && error.name === 'NotFoundError') {
        return { items: [] };
      }
      throw error;
    }
    const file = await fileHandle.getFile();
    return HierarchySchemaDto.parse(JSON.parse(await file.text()));
  }

  async saveHierarchy({ hierarchy }: { hierarchy: HierarchyDto }): Promise<void> {
    await this.ensureRoot();
    const fileHandle = await this.root!.getFileHandle('hierarchy.json', { create: true }) as FileSystemFileHandleWithWritable;
    const writable = await fileHandle.createWritable();
    await writable.write(JSON.stringify(hierarchy));
    await writable.close();
  }

  // --- Persistence Implementation ---

  async saveChatMeta({ meta }: { meta: ChatMeta }): Promise<void> {
    const dto = chatMetaToDto({ domain: meta });
    const serialized = JSON.stringify(ChatMetaSchemaDto.parse(dto));
    const dir = await this.getDir({ name: 'chat-metas' });
    const fileHandle = await dir.getFileHandle(`${idToRaw({ id: meta.id })}.json`, { create: true }) as FileSystemFileHandleWithWritable;
    const writable = await fileHandle.createWritable();
    await writable.write(serialized);
    await writable.close();
  }

  async saveChatContent({ id, content }: { id: ChatId, content: ChatContent }): Promise<void> {
    const dto = chatContentToDto({ domain: content });
    ChatContentSchemaDto.parse(dto);
    const dir = await this.getDir({ name: 'chat-contents' });
    const fileHandle = await dir.getFileHandle(`${idToRaw({ id })}.json`, { create: true }) as FileSystemFileHandleWithWritable;
    const writable = await fileHandle.createWritable();
    await writable.write(JSON.stringify(dto));
    await writable.close();
  }

  async loadChat({ id }: { id: ChatId }): Promise<Chat | null> {
    const rawMeta = await this.readChatRecord({ directory: 'chat-metas', id });
    const rawContent = await this.readChatRecord({ directory: 'chat-contents', id });
    const meta = rawMeta === null ? null : ChatMetaSchemaDto.parse(JSON.parse(rawMeta));
    const content = rawContent === null ? null : ChatContentSchemaDto.parse(JSON.parse(rawContent));
    if (meta === null || content === null) {
      return null;
    }

    const chat = chatToDomain({ dto: { ...meta, ...content, experimental: meta.experimental, messages: undefined } });

    // Resolve groupId from hierarchy
    const hierarchy = await this.loadHierarchy();
    if (hierarchy) {
      const group = hierarchy.items.find(i => i.type === 'chat_group' && i.chat_ids.includes(idToRaw({ id })));
      if (group) {
        chat.groupId = toChatGroupId({ raw: group.id });
      }
    }

    // Hydrate attachments with metadata from BinaryObject indices
    await this.hydrateAttachments({ nodes: chat.root.items });

    return chat;
  }

  async loadChatMeta({ id }: { id: ChatId }): Promise<ChatMeta | null> {
    const rawMeta = await this.readChatRecord({ directory: 'chat-metas', id });
    if (rawMeta === null) {
      return null;
    }
    const meta = chatMetaToDomain({ dto: ChatMetaSchemaDto.parse(JSON.parse(rawMeta)) });

    // Resolve groupId from hierarchy
    const hierarchy = await this.loadHierarchy();
    if (hierarchy) {
      const group = hierarchy.items.find(i => i.type === 'chat_group' && i.chat_ids.includes(idToRaw({ id })));
      if (group) {
        meta.groupId = toChatGroupId({ raw: group.id });
      }
    }

    return meta;
  }

  async loadChatContent({ id }: { id: ChatId }): Promise<ChatContent | null> {
    const content = await this.loadUnhydratedChatContent({ id });
    if (content === null) {
      return null;
    }

    await this.hydrateAttachments({ nodes: content.root.items });
    return content;
  }

  async loadChatContentWithoutAttachments({ id }: { id: ChatId }): Promise<ChatContent | null> {
    return this.loadUnhydratedChatContent({ id });
  }

  async deleteChat({ id }: { id: ChatId }): Promise<void> {
    try {
      const metaDir = await this.getDir({ name: 'chat-metas' });
      const contentDir = await this.getDir({ name: 'chat-contents' });
      await metaDir.removeEntry(`${idToRaw({ id })}.json`);
      await contentDir.removeEntry(`${idToRaw({ id })}.json`);
    } catch { /* ignore */ }
  }

  async saveChatGroup({ chatGroup }: { chatGroup: ChatGroup }): Promise<void> {
    const dto = chatGroupToDto({ domain: chatGroup });
    const serialized = JSON.stringify(ChatGroupSchemaDto.parse(dto));
    const dir = await this.getDir({ name: 'chat-groups' });
    const fileHandle = await dir.getFileHandle(`${idToRaw({ id: chatGroup.id })}.json`, { create: true }) as FileSystemFileHandleWithWritable;
    const writable = await fileHandle.createWritable();
    await writable.write(serialized);
    await writable.close();
  }

  async loadChatGroupConfiguration({ id }: { id: ChatGroupId }): Promise<Omit<ChatGroup, 'items'> | null> {
    await this.ensureRoot();
    const dir = await this.getDir({ name: 'chat-groups' });
    let handle: FileSystemFileHandle;
    try {
      handle = await dir.getFileHandle(`${idToRaw({ id })}.json`);
    } catch (error) {
      // An acquired file that later fails to read is not a missing group.
      if ((error instanceof DOMException || error instanceof Error) && error.name === 'NotFoundError') {
        return null;
      }
      throw error;
    }
    const groupDto = ChatGroupSchemaDto.parse(JSON.parse(await (await handle.getFile()).text()));
    return chatGroupConfigurationToDomain({ dto: groupDto });
  }

  async loadChatGroup({ id }: { id: ChatGroupId }): Promise<ChatGroup | null> {
    await this.ensureRoot();
    const dir = await this.getDir({ name: 'chat-groups' });
    let handle: FileSystemFileHandle;
    try {
      handle = await dir.getFileHandle(`${idToRaw({ id })}.json`);
    } catch (error) {
      // An acquired file that later fails to read is not a missing group.
      if ((error instanceof DOMException || error instanceof Error) && error.name === 'NotFoundError') {
        return null;
      }
      throw error;
    }
    const groupDto = ChatGroupSchemaDto.parse(JSON.parse(await (await handle.getFile()).text()));
    const { hierarchy, allMetas } = await promiseAllKeyed({
      hierarchy: this.loadHierarchy(),
      allMetas: this.listChatMetasRaw(),
    });
    const chatMetas = allMetas.map(dto => chatMetaToDomain({ dto }));
    const h = hierarchyToDomain({ dto: hierarchy || { items: [] } });
    return chatGroupToDomain({ dto: groupDto, hierarchy: h, chatMetas });
  }

  async deleteChatGroup({ id }: { id: ChatGroupId }): Promise<void> {
    try {
      const dir = await this.getDir({ name: 'chat-groups' });
      await dir.removeEntry(`${idToRaw({ id })}.json`);
    } catch { /* ignore */ }
  }

  public override async getSidebarStructure(): Promise<SidebarItem[]> {
    const { rawHierarchy, rawMetas, rawGroups } = await promiseAllKeyed({
      rawHierarchy: this.loadHierarchy(),
      rawMetas: this.listChatMetasRaw(),
      rawGroups: this.listChatGroupsRaw(),
    });

    const hierarchy = hierarchyToDomain({ dto: rawHierarchy || { items: [] } });
    const chatMetas = rawMetas.map(dto => chatMetaToDomain({ dto }));
    const chatGroups = rawGroups.map(dto => chatGroupToDomain({ dto, hierarchy, chatMetas }));

    return buildSidebarItemsFromHierarchy({ hierarchy, chatMetas, chatGroups });
  }

  // --- Binary Object Storage ---

  private async saveFileWithMetadata({ blob, binaryObjectId, name, mimeType, createdAt }: {
    blob: Blob,
    binaryObjectId: BinaryObjectId,
    name: string,
    mimeType: string | undefined,
    createdAt: number,
  }): Promise<void> {
    const shard = this.getBinaryObjectShardPath({ id: binaryObjectId });
    const dir = await this.getShardDir({ shard: shard });

    // Do not touch an existing body if its shard metadata cannot be read safely.
    const index = await this.loadShardIndex({ shard });

    // 1. Write Blob
    const binFileName = `${idToRaw({ id: binaryObjectId })}.bin`;
    const fileHandle = await dir.getFileHandle(binFileName, { create: true }) as FileSystemFileHandleWithWritable;
    const writable = await fileHandle.createWritable();
    // Convert blob to ArrayBuffer for compatibility
    await writable.write(await blob.arrayBuffer());
    await writable.close();

    // 2. Write Marker
    const markerName = `.${binFileName}.complete`;
    await dir.getFileHandle(markerName, { create: true });

    // 3. Update Index
    index.objects[idToRaw({ id: binaryObjectId })] = {
      id: idToRaw({ id: binaryObjectId }),
      mimeType: mimeType ?? (blob.type || 'application/octet-stream'),
      size: blob.size,
      createdAt,
      name,
    };
    await this.saveShardIndex({ shard: shard, index: index });
  }

  async saveFile({ blob, binaryObjectId, name, mimeType }: {
    blob: Blob,
    binaryObjectId: BinaryObjectId,
    name: string,
    mimeType?: string,
  }): Promise<void> {
    await this.saveFileWithMetadata({
      blob,
      binaryObjectId,
      name,
      mimeType,
      createdAt: Date.now(),
    });
  }

  async getFile({ binaryObjectId }: { binaryObjectId: BinaryObjectId }): Promise<Blob | null> {
    try {
      const shard = this.getBinaryObjectShardPath({ id: binaryObjectId });
      const dir = await this.getShardDir({ shard: shard });
      const rawId = idToRaw({ id: binaryObjectId });
      const fileName = `${rawId}.bin`;
      const markerName = `.${fileName}.complete`;

      // Verify completion marker
      await dir.getFileHandle(markerName);

      const fileHandle = await dir.getFileHandle(fileName);
      const { file, index } = await promiseAllKeyed({
        file: fileHandle.getFile(),
        index: this.loadShardIndex({ shard: shard }),
      });
      const mimeType = index.objects[rawId]?.mimeType;

      return mimeType === undefined || mimeType === file.type
        ? file
        : file.slice(0, file.size, mimeType);
    } catch (e) {
      console.error('Failed to get file from OPFS storage:', e);
      return null;
    }
  }

  async getBinaryObject({ binaryObjectId }: { binaryObjectId: BinaryObjectId }): Promise<BinaryObject | null> {
    try {
      const shard = this.getBinaryObjectShardPath({ id: binaryObjectId });
      const index = await this.loadShardIndex({ shard: shard });
      const dto = index.objects[idToRaw({ id: binaryObjectId })];
      return dto === undefined ? null : binaryObjectToDomain({ dto });
    } catch (e) {
      console.error('Failed to get binary object info:', e);
      return null;
    }
  }

  async hasAttachments(): Promise<boolean> {
    try {
      const baseDir = await this.getBinaryObjectsDir();
      for await (const entry of baseDir.values()) {
        const kind = entry.kind;
        switch (kind) {
        case 'directory': {
          // Check if shard has any files other than index.json
          for await (const shardEntry of (entry as FileSystemDirectoryHandle).values()) {
            if (shardEntry.name !== 'index.json') {
              return true;
            }
          }
          break;
        }
        case 'file':
          break;
        default: {
          const _ex: never = kind;
          throw new Error(`Unhandled entry kind: ${_ex}`);
        }
        }
      }
      return false;
    } catch {
      return false;
    }
  }

  async *listBinaryObjects(): AsyncIterable<BinaryObject> {
    await this.ensureRoot();
    try {
      const baseDir = await this.getBinaryObjectsDir();
      for await (const shardEntry of baseDir.values()) {
        const kind = shardEntry.kind;
        switch (kind) {
        case 'directory': {
          const index = await this.loadShardIndex({ shard: shardEntry.name });
          for (const obj of Object.values(index.objects)) {
            yield binaryObjectToDomain({ dto: obj });
          }
          break;
        }
        case 'file':
          break;
        default: {
          const _ex: never = kind;
          throw new Error(`Unhandled entry kind: ${_ex}`);
        }
        }
      }
    } catch (e) {
      console.error('[OPFSStorageProvider] Failed to list binary objects', e);
    }
  }

  async deleteBinaryObject({ binaryObjectId }: { binaryObjectId: BinaryObjectId }): Promise<void> {
    await this.ensureRoot();
    const shard = this.getBinaryObjectShardPath({ id: binaryObjectId });
    const dir = await this.getShardDir({ shard: shard });
    const fileName = `${idToRaw({ id: binaryObjectId })}.bin`;
    const markerName = `.${fileName}.complete`;

    try {
      await dir.removeEntry(fileName);
    } catch (error) {
      if (!((error instanceof DOMException || error instanceof Error) && error.name === 'NotFoundError')) {
        throw error;
      }
    }
    try {
      await dir.removeEntry(markerName);
    } catch (error) {
      if (!((error instanceof DOMException || error instanceof Error) && error.name === 'NotFoundError')) {
        throw error;
      }
    }

    const index = await this.loadShardIndex({ shard: shard });
    if (index.objects[idToRaw({ id: binaryObjectId })]) {
      delete index.objects[idToRaw({ id: binaryObjectId })];
      await this.saveShardIndex({ shard: shard, index: index });
    }
  }

  async saveSettings({ settings }: { settings: Settings }): Promise<void> {
    const dto = settingsToDto({ domain: settings });
    const serialized = JSON.stringify(SettingsSchemaDto.parse(dto));
    await this.ensureRoot();
    const fileHandle = await this.root!.getFileHandle('settings.json', { create: true }) as FileSystemFileHandleWithWritable;
    const writable = await fileHandle.createWritable();
    await writable.write(serialized);
    await writable.close();
  }

  async loadSettings(): Promise<Settings | null> {
    await this.ensureRoot();
    try {
      const fileHandle = await this.root!.getFileHandle('settings.json');
      const file = await fileHandle.getFile();
      return settingsToDomain({ dto: SettingsSchemaDto.parse(JSON.parse(await file.text())) });
    } catch (error) {
      // Unavailable RPC bytes must not masquerade as absent settings.
      return null;
    }
  }

  async clearAll(): Promise<void> {
    await this.withVolumeFilesLock({
      operation: async () => {
        await this.ensureRoot();
        for await (const key of this.root!.keys()) {
          await this.root!.removeEntry(key, { recursive: true });
        }
      },
    });
  }

  // --- Migration Implementation ---

  async dump(): Promise<StorageSnapshot> {
    await this.ensureRoot();
    const { settings, hierarchy, rawMetas, rawGroups } = await promiseAllKeyed({
      settings: this.loadSettings(),
      hierarchy: this.loadHierarchy(),
      rawMetas: this.listChatMetasRaw(),
      rawGroups: this.listChatGroupsRaw(),
    });

    const h = hierarchyToDomain({ dto: hierarchy || { items: [] } });
    const chatGroups = rawGroups.map(dto => chatGroupToDomain({ dto, hierarchy: h, chatMetas: [] }));
    const chatMetas = rawMetas.map(dto => chatMetaToDomain({ dto }));

    const contentStream = async function* (this: OPFSStorageProvider): AsyncGenerator<MigrationChunkDto> {
      // 1. Stream all chats
      for (const meta of rawMetas) {
        const chat = await this.loadChat({ id: toChatId({ raw: meta.id }) });
        if (chat) {
          yield { type: 'chat' as const, data: chatToDto({ domain: chat }) };
        }
      }

      // 2. Stream all binary objects directly from storage (independent of chat references)
      // Existing index read failures must abort export instead of finalizing a
      // partial backup. Only a missing index is handled by loadShardIndex.
      const baseDir = await this.getBinaryObjectsDir();
      for await (const shardEntry of baseDir.values()) {
        const kind = shardEntry.kind;
        switch (kind) {
        case 'directory': {
          const shard = shardEntry.name;
          const index = await this.loadShardIndex({ shard: shard });
          for (const bId of Object.keys(index.objects)) {
            const meta = index.objects[bId]!;
            const blob = await this.getFile({ binaryObjectId: toBinaryObjectId({ raw: bId }) });
            if (blob) {
              yield {
                type: 'binary_object' as const,
                id: bId,
                name: meta.name ?? 'file',
                mimeType: meta.mimeType,
                size: meta.size,
                createdAt: meta.createdAt,
                blob,
              };
            }
          }
          break;
        }
        case 'file':
          break;
        default: {
          const _ex: never = kind;
          throw new Error(`Unhandled entry kind: ${_ex}`);
        }
        }
      }
    };

    return {
      structure: {
        settings: settings || {
          titleGeneration: { endpoint: 'same_scope', model: 'same_scope', lmParameters: { temperature: undefined, topP: undefined, maxCompletionTokens: undefined, presencePenalty: undefined, frequencyPenalty: undefined, stop: undefined, reasoning: { effort: undefined } } },
          providerProfiles: [],
          mounts: [],
          storageType: 'opfs',
          endpoint: { type: 'openai', url: '' },
        } satisfies Settings,
        hierarchy: h,
        chatMetas,
        chatGroups,
      },
      contentStream: contentStream.call(this),
    };
  }

  async restore({ snapshot }: { snapshot: StorageSnapshot }): Promise<void> {
    const { structure, contentStream } = snapshot;
    await this.ensureRoot();

    // 1. Restore Structural Metadata
    if (structure.settings) {
      await this.saveSettings({ settings: structure.settings });
    }
    if (structure.hierarchy) {
      await this.saveHierarchy({ hierarchy: hierarchyToDto({ domain: structure.hierarchy }) });
    }
    if (structure.chatMetas) {
      for (const meta of structure.chatMetas) {
        await this.saveChatMeta({ meta });
      }
    }
    if (structure.chatGroups) {
      for (const group of structure.chatGroups) {
        await this.saveChatGroup({ chatGroup: group });
      }
    }

    // 2. Restore Heavy Content
    for await (const chunk of contentStream) {
      const type = chunk.type;
      switch (type) {
      case 'chat': {
        const domainChat = chatToDomain({ dto: chunk.data });
        await this.saveChatContent({ id: domainChat.id, content: domainChat });
        await this.saveChatMeta({ meta: domainChat });
        break;
      }
      case 'binary_object':
        await this.saveFileWithMetadata({
          blob: chunk.blob,
          binaryObjectId: toBinaryObjectId({ raw: chunk.id }),
          name: chunk.name,
          mimeType: chunk.mimeType,
          createdAt: chunk.createdAt,
        });
        break;
      default: {
        const _ex: never = type;
        throw new Error(`Unknown chunk type: ${_ex}`);
      }
      }
    }
  }

  // --- Volume Management ---

  private readonly hostVolumeDB = new HostVolumeDB();

  private getVolumeShardPath({ id }: { id: VolumeId }): string {
    return idToRaw({ id }).slice(-2).toLowerCase();
  }

  private async getVolumesBaseDir(): Promise<FileSystemDirectoryHandle> {
    return await this.getDir({ name: 'volumes' });
  }

  private async findVolumesBaseDir(): Promise<FileSystemDirectoryHandle | undefined> {
    await this.ensureRoot();
    try {
      return await this.root!.getDirectoryHandle('volumes');
    } catch (error) {
      if (isVolumeEntryNotFound({ error })) {
        return undefined;
      }
      throw error;
    }
  }

  private async getVolumeShardDir({ shard }: { shard: string }): Promise<FileSystemDirectoryHandle> {
    const baseDir = await this.getVolumesBaseDir();
    return await this.getDir({ name: shard, parent: baseDir });
  }

  private async findVolumeShardDir({ shard }: { shard: string }): Promise<FileSystemDirectoryHandle | undefined> {
    const baseDir = await this.findVolumesBaseDir();
    if (baseDir === undefined) {
      return undefined;
    }
    try {
      return await baseDir.getDirectoryHandle(shard);
    } catch (error) {
      if (isVolumeEntryNotFound({ error })) {
        return undefined;
      }
      throw error;
    }
  }

  private async withVolumeFilesLock<T>({ operation }: { operation(): Promise<T> }): Promise<T> {
    const synchronizer = await getVolumeSynchronizer();
    // Serialize root creation/removal and metadata publication, never file-byte
    // copying. Publication can then validate its captured root before indexing it.
    return await synchronizer.withLock({ lockKey: 'naidan-volume-files', fn: operation });
  }

  private async loadVolumeShardIndex({ shard }: { shard: string }): Promise<VolumeIndexDto> {
    const dir = await this.findVolumeShardDir({ shard });
    if (dir === undefined) {
      return { volumes: {} };
    }
    let fileHandle: FileSystemFileHandle;
    try {
      fileHandle = await dir.getFileHandle('index.json');
    } catch (error) {
      if (isVolumeEntryNotFound({ error })) {
        return { volumes: {} };
      }
      throw error;
    }
    const file = await fileHandle.getFile();
    return VolumeIndexSchemaDto.parse(JSON.parse(await file.text()));
  }

  private async withVolumeIndexLock({ shard, operation }: {
    shard: string,
    operation(): Promise<void>,
  }): Promise<void> {
    await this.withVolumeFilesLock({
      operation: async () => {
        const synchronizer = await getVolumeSynchronizer();
        await synchronizer.withLock({
          // Keep this below outer chat/settings locks. Using LOCK_METADATA again
          // would deadlock callers that already hold that lock during cleanup.
          lockKey: `naidan-volume-index:${shard}`,
          fn: operation,
        });
      },
    });
  }

  private async updateVolumeShardIndex({ shard, update }: {
    shard: string,
    update({ index }: { index: VolumeIndexDto }): Promise<void>,
  }): Promise<void> {
    await this.withVolumeIndexLock({
      shard,
      operation: async () => {
        const index = await this.loadVolumeShardIndex({ shard });
        await update({ index });
        await this.saveVolumeShardIndex({ shard, index });
      },
    });
  }

  private async createVolumeDirectory({ volumeId }: { volumeId: VolumeId }): Promise<{ root: FileSystemDirectoryHandle, parent: FileSystemDirectoryHandle }> {
    const shard = this.getVolumeShardPath({ id: volumeId });
    const parent = await this.getVolumeShardDir({ shard });
    const root = await parent.getDirectoryHandle(idToRaw({ id: volumeId }), { create: true });
    return { root, parent };
  }

  override async ensureVolumeDirectory({ volumeId }: { volumeId: VolumeId }): Promise<FileSystemDirectoryHandle> {
    return await this.withVolumeFilesLock({ operation: async () => (await this.createVolumeDirectory({ volumeId })).root });
  }

  override async publishVolume({ volume, root }: { volume: Volume, root: FileSystemDirectoryHandle | undefined }): Promise<void> {
    const shard = this.getVolumeShardPath({ id: volume.id });
    await this.withVolumeIndexLock({
      shard,
      operation: async () => {
        switch (volume.type) {
        case 'opfs': {
          const directory = await this.findVolumeShardDir({ shard });
          if (directory === undefined) {
            throw new DOMException('The browser folder no longer exists', 'NotFoundError');
          }
          const current = await directory.getDirectoryHandle(idToRaw({ id: volume.id }));
          // A path reused after clearAll must not publish an older copy task.
          if (root !== undefined && !await current.isSameEntry(root)) {
            throw new DOMException('The browser folder was replaced before publication', 'InvalidStateError');
          }
          break;
        }
        case 'host':
          await this.getVolumeShardDir({ shard });
          break;
        default: {
          const exhaustive: never = volume.type;
          throw new Error(`Unhandled volume type: ${String(exhaustive)}`);
        }
        }
        const index = await this.loadVolumeShardIndex({ shard });
        index.volumes[idToRaw({ id: volume.id })] = volumeToDto({ domain: volume });
        await this.saveVolumeShardIndex({ shard, index });
      },
    });
  }

  override async probeVolumeAccess({ volumeId }: { volumeId: VolumeId }): Promise<StoredVolumeAccess> {
    const shard = this.getVolumeShardPath({ id: volumeId });
    const index = await this.loadVolumeShardIndex({ shard });
    const dto = index.volumes[idToRaw({ id: volumeId })];
    if (dto !== undefined) {
      switch (dto.type) {
      case 'host': {
        const handle = await this.hostVolumeDB.get({ id: idToRaw({ id: volumeId }) });
        return handle ? { kind: 'native', volume: volumeToDomain({ dto }), handle } : { kind: 'missing', volumeId };
      }
      case 'opfs':
        break;
      default: {
        const exhaustive: never = dto;
        throw new Error(`Unhandled volume type: ${String(exhaustive)}`);
      }
      }
    }
    const shardDir = await this.findVolumeShardDir({ shard });
    if (shardDir === undefined) {
      return { kind: 'missing', volumeId };
    }
    let handle: FileSystemDirectoryHandle;
    try {
      handle = await shardDir.getDirectoryHandle(idToRaw({ id: volumeId }));
    } catch (error) {
      if (isVolumeEntryNotFound({ error })) {
        return { kind: 'missing', volumeId };
      }
      throw error;
    }
    return dto === undefined ? { kind: 'incomplete', volumeId, handle } : { kind: 'native', volume: volumeToDomain({ dto }), handle };
  }

  override async deleteUnpublishedVolumeDirectory({ volumeId }: { volumeId: VolumeId }): Promise<void> {
    const shard = this.getVolumeShardPath({ id: volumeId });
    await this.withVolumeIndexLock({
      shard,
      operation: async () => {
        const index = await this.loadVolumeShardIndex({ shard });
        if (index.volumes[idToRaw({ id: volumeId })] !== undefined) {
          throw new DOMException('The folder has already been published', 'InvalidStateError');
        }
        const shardDir = await this.findVolumeShardDir({ shard });
        if (shardDir === undefined) {
          return;
        }
        try {
          await shardDir.removeEntry(idToRaw({ id: volumeId }), { recursive: true });
        } catch (error) {
          if (!isVolumeEntryNotFound({ error })) {
            throw error;
          }
        }
      },
    });
  }

  private async saveVolumeShardIndex({ shard, index }: { shard: string, index: VolumeIndexDto }): Promise<void> {
    const dir = await this.findVolumeShardDir({ shard });
    if (dir === undefined) {
      throw new DOMException('The browser folder index no longer exists', 'NotFoundError');
    }
    const fileHandle = await dir.getFileHandle('index.json', { create: true }) as FileSystemFileHandleWithWritable;
    const writable = await fileHandle.createWritable();
    try {
      await writable.write(JSON.stringify(index));
      await writable.close();
    } catch (error) {
      try {
        await writable.abort(error);
      } catch { /* Preserve the metadata failure, including if abort also fails. */ }
      throw error;
    }
  }

  private async copyDirectory({ source, destination }: { source: FileSystemDirectoryHandle, destination: FileSystemDirectoryHandle }): Promise<void> {
    for await (const entry of source.values()) {
      switch (entry.kind) {
      case 'file': {
        const file = await (entry as FileSystemFileHandle).getFile();
        const destFile = await destination.getFileHandle(entry.name, { create: true }) as FileSystemFileHandleWithWritable;
        const writable = await destFile.createWritable();
        await writable.write(await file.arrayBuffer());
        await writable.close();
        break;
      }
      case 'directory': {
        const newDestSubDir = await destination.getDirectoryHandle(entry.name, { create: true });
        await this.copyDirectory({ source: entry as FileSystemDirectoryHandle, destination: newDestSubDir });
        break;
      }
      default: {
        const _ex: never = entry;
        throw new Error(`Unhandled entry kind: ${(_ex as { kind: string }).kind}`);
      }
      }
    }
  }

  async *listVolumes(): AsyncIterable<Volume> {
    await this.ensureRoot();
    try {
      const baseDir = await this.findVolumesBaseDir();
      if (baseDir === undefined) {
        return;
      }
      for await (const shardEntry of baseDir.values()) {
        switch (shardEntry.kind) {
        case 'directory': {
          const index = await this.loadVolumeShardIndex({ shard: shardEntry.name });
          for (const volDto of Object.values(index.volumes)) {
            yield volumeToDomain({ dto: volDto });
          }
          break;
        }
        case 'file':
          break;
        default: {
          throw new Error(`Unhandled entry kind: ${((shardEntry satisfies never) as { readonly kind: string }).kind}`);
        }
        }
      }
    } catch (e) {
      console.error('[OPFSStorageProvider] Failed to list volumes', e);
    }
  }

  async createVolume({ name, type, sourceHandle }: {
    name: string,
    type: VolumeType,
    sourceHandle: FileSystemDirectoryHandle,
  }): Promise<Volume> {
    const id = generateId<VolumeId>();
    const createdAt = Date.now();

    let volumeDto: VolumeDto;
    let volumeRoot: FileSystemDirectoryHandle | undefined;

    switch (type) {
    case 'opfs': {
      volumeRoot = await this.ensureVolumeDirectory({ volumeId: id });
      await this.copyDirectory({ source: sourceHandle, destination: volumeRoot });

      volumeDto = {
        type: 'opfs',
        id: idToRaw({ id }),
        name,
        createdAt,
      };
      break;
    }
    case 'host': {
      await this.hostVolumeDB.put({ id: idToRaw({ id }), handle: sourceHandle });
      volumeDto = {
        type: 'host',
        id: idToRaw({ id }),
        name,
        createdAt,
      };
      break;
    }
    default: {
      const _ex: never = type;
      throw new Error(`Unhandled volume type: ${(_ex as { type: string }).type}`);
    }
    }

    const volume = volumeToDomain({ dto: volumeDto });
    await this.publishVolume({ volume, root: volumeRoot });
    return volume;
  }

  async createVolumeFromFiles({ name, entries, onProgress, signal }: {
    name: string,
    entries: Array<{ file: File, relativePath: string }>,
    onProgress?: ({ processed, total }: { processed: number, total: number }) => void,
    signal?: AbortSignal,
  }): Promise<Volume> {
    const id = generateId<VolumeId>();
    const createdAt = Date.now();

    const { root: volumeDir, parent: shardDir } = await this.withVolumeFilesLock({ operation: () => this.createVolumeDirectory({ volumeId: id }) });

    for (let i = 0; i < entries.length; i++) {
      if (signal?.aborted) {
        await shardDir.removeEntry(idToRaw({ id }), { recursive: true }).catch(() => {});
        throw new DOMException('Cancelled by user', 'AbortError');
      }

      const entry = entries[i];
      if (!entry) {
        continue;
      }
      const { file, relativePath } = entry;
      const pathParts = relativePath.split('/').filter(Boolean);

      const fileName = pathParts.pop()!;
      let currentDir = volumeDir;

      for (const part of pathParts) {
        currentDir = await currentDir.getDirectoryHandle(part, { create: true });
      }

      const fileHandle = await currentDir.getFileHandle(fileName, { create: true }) as FileSystemFileHandleWithWritable;
      const writable = await fileHandle.createWritable();
      await writable.write(await file.arrayBuffer());
      await writable.close();

      if (onProgress) {
        onProgress({ processed: i + 1, total: entries.length });
      }
    }

    const volumeDto: VolumeDto = {
      type: 'opfs',
      id: idToRaw({ id }),
      name,
      createdAt,
    };

    const volume = volumeToDomain({ dto: volumeDto });
    await this.publishVolume({ volume, root: volumeDir });
    return volume;
  }

  async getVolumeDirectoryHandle({ volumeId }: { volumeId: VolumeId }): Promise<FileSystemDirectoryHandle | null> {
    try {
      const access = await this.probeVolumeAccess({ volumeId });
      switch (access.kind) {
      case 'native':
        return access.handle;
      case 'incomplete':
      case 'missing':
        return null;
      default: {
        const _ex: never = access;
        throw new Error(`Unhandled volume type: ${JSON.stringify(_ex)}`);
      }
      }
    } catch (e) {
      console.error('Failed to get volume directory handle:', e);
      return null;
    }
  }

  async renameVolume({ volumeId, name }: { volumeId: VolumeId, name: string }): Promise<void> {
    const shard = this.getVolumeShardPath({ id: volumeId });
    await this.updateVolumeShardIndex({
      shard,
      update: async ({ index }) => {
        const volume = index.volumes[idToRaw({ id: volumeId })];
        if (!volume) {
          throw new Error(`Volume not found: ${idToRaw({ id: volumeId })}`);
        }
        index.volumes[idToRaw({ id: volumeId })] = { ...volume, name };
      },
    });
  }

  async deleteVolume({ volumeId }: { volumeId: VolumeId }): Promise<void> {
    const shard = this.getVolumeShardPath({ id: volumeId });

    try {
      await this.withVolumeIndexLock({
        shard,
        operation: async () => {
          const index = await this.loadVolumeShardIndex({ shard });
          const volume = index.volumes[idToRaw({ id: volumeId })];
          if (volume === undefined) {
            return;
          }
          switch (volume.type) {
          case 'opfs': {
            const shardDir = await this.getVolumeShardDir({ shard });
            await shardDir.removeEntry(idToRaw({ id: volumeId }), { recursive: true });
            break;
          }
          case 'host':
            await this.hostVolumeDB.delete({ id: idToRaw({ id: volumeId }) });
            break;
          default: {
            const _ex: never = volume;
            throw new Error(`Unhandled volume type: ${JSON.stringify(_ex)}`);
          }
          }

          delete index.volumes[idToRaw({ id: volumeId })];
          await this.saveVolumeShardIndex({ shard, index });
        },
      });
    } catch (e) {
      console.error('Failed to delete volume:', e);
    }
  }
}

class HostVolumeDB {
  private readonly DB_NAME = 'naidan-volumes';
  private readonly STORE_NAME = 'handles';

  async put({ id, handle }: { id: string, handle: FileSystemDirectoryHandle }): Promise<void> {
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.STORE_NAME, 'readwrite');
      const store = tx.objectStore(this.STORE_NAME);
      const req = store.put(handle, id);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  async get({ id }: { id: string }): Promise<FileSystemDirectoryHandle | undefined> {
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.STORE_NAME, 'readonly');
      const store = tx.objectStore(this.STORE_NAME);
      const req = store.get(id);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  async delete({ id }: { id: string }): Promise<void> {
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.STORE_NAME, 'readwrite');
      const store = tx.objectStore(this.STORE_NAME);
      const req = store.delete(id);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  private open(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(this.DB_NAME, 1);
      req.onupgradeneeded = (e) => {
        const db = (e.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.STORE_NAME)) {
          db.createObjectStore(this.STORE_NAME);
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
}

// Export internal state and logic used only for testing here. Do not reference these in production logic.
// ESLint-required for TypeScript modules.
export const TEST_ONLY = {
};
